import { useAuth } from "@/contexts/AuthContext";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API from "@/lib/api";
import Header from "@/components/app/Header";
import FeeAlertBanner from "@/components/app/FeeAlertBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import BulkActionBar from "@/components/app/BulkActionBar";
import { InstructDialog } from "@/components/app/InstructDialog";
import {
  Calculator, MagnifyingGlass, Trash, ArrowRight,
  Copy, User, CurrencyGbp, Warning, Receipt, GearSix, LinkSimple, Plus, X,
  Phone, EnvelopeSimple, CheckCircle, CircleDashed, ArrowsClockwise,
  ArrowsClockwise as FollowUpIcon, Prohibit, Archive as ArchiveIcon, ArrowCounterClockwise,
} from "@phosphor-icons/react";

function formatCurrency(n) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(n);
}

/**
 * Per-client contact strip for a quote row. Renders the primary client +
 * any additional clients, each on its own line with name + click-to-call
 * phone link + click-to-email link. Click handlers stopPropagation so the
 * solicitor can dial / email without triggering the row's nav handler.
 */
function QuoteContacts({ q }) {
  const all = [
    { name: q.client_name, email: q.client_email, phone: q.client_phone },
    ...(Array.isArray(q.additional_clients) ? q.additional_clients : []),
  ].filter((c) => (c.name || "").trim() || c.email || c.phone);
  if (all.length === 0) return null;
  return (
    <div className="mt-1 space-y-0.5" data-testid={`quote-contacts-${q.id}`}>
      {all.map((c, idx) => (
        <div key={idx} className="flex items-center gap-2 text-xs text-slate-500 flex-wrap" data-testid={`quote-contact-${q.id}-${idx}`}>
          <span className="font-medium text-slate-600">{c.name || "—"}</span>
          {c.phone && (
            <a
              href={`tel:${(c.phone || "").replace(/\s/g, "")}`}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 hover:text-slate-800 hover:underline"
              data-testid={`quote-phone-${q.id}-${idx}`}
            >
              <Phone weight="bold" className="w-3 h-3" />
              {c.phone}
            </a>
          )}
          {c.email && (
            <a
              href={`mailto:${c.email}`}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 hover:text-slate-800 hover:underline truncate max-w-[260px]"
              data-testid={`quote-email-${q.id}-${idx}`}
            >
              <EnvelopeSimple weight="bold" className="w-3 h-3" />
              {c.email}
            </a>
          )}
        </div>
      ))}
    </div>
  );
}


const CUSTOM_FEE_TXN_TYPES = [
  { value: "buying", label: "Buying" },
  { value: "selling", label: "Selling" },
  { value: "both", label: "Buy & Sell" },
  { value: "remortgage", label: "Remortgage" },
  { value: "transfer", label: "Transfer of Equity" },
  { value: "grant_of_lease", label: "Grant of Lease" },
];

const CUSTOM_FEE_CATEGORY_LABEL = {
  legal: "custom legal fee",
  supplementary: "custom supplementary fee",
  disbursement: "custom disbursement",
};

const CUSTOM_FEE_CATEGORY_ACCENT = {
  legal: "bg-rose-50 text-rose-700 border-rose-200",
  supplementary: "bg-cyan-50 text-cyan-700 border-cyan-200",
  disbursement: "bg-lime-50 text-lime-700 border-lime-200",
};

/**
 * Inline editor for admin-defined custom fees / disbursements within the
 * Quote Fee Settings dialog. Renders one block per ``category`` (legal,
 * supplementary, disbursement). Each existing item shows name + £ amount
 * + transaction-type chips + delete; a "+ Add" button reveals an inline
 * form to create a new one. All persistence goes through dedicated
 * /api/quote-settings/custom-fees endpoints (kept separate from the main
 * PUT /quote-settings flow so the array isn't accidentally overwritten).
 */
function CustomFeesSection({ category, customFees, onChange }) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [types, setTypes] = useState([]);
  const [busy, setBusy] = useState(false);

  const items = (customFees || []).filter((cf) => cf.category === category);
  const accent = CUSTOM_FEE_CATEGORY_ACCENT[category];
  const labelWord = CUSTOM_FEE_CATEGORY_LABEL[category];

  const reset = () => { setName(""); setAmount(""); setTypes([]); setAdding(false); };

  const toggleType = (t) => setTypes((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    if (!amount || Number(amount) < 0) { toast.error("Enter a valid amount"); return; }
    if (types.length === 0) { toast.error("Pick at least one transaction type"); return; }
    setBusy(true);
    try {
      const { data } = await API.post("/quote-settings/custom-fees", {
        name: name.trim(), category, amount: Number(amount), transaction_types: types,
      });
      onChange(data.custom_fees);
      toast.success("Custom item added");
      reset();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add");
    } finally {
      setBusy(false);
    }
  };

  const handleEdit = async (id, patch) => {
    try {
      const { data } = await API.put(`/quote-settings/custom-fees/${id}`, patch);
      onChange(data.custom_fees);
    } catch (err) {
      // Stale row — probably deleted in another tab. Silently re-sync from
      // the canonical /quote-settings rather than scaring the user with a
      // 404 toast on what looks like a normal blur action.
      if (err.response?.status === 404) {
        try {
          const { data } = await API.get("/quote-settings");
          onChange(data.custom_fees || []);
        } catch {}
        return;
      }
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this custom item? It will be removed from all future quotes.")) return;
    try {
      const { data } = await API.delete(`/quote-settings/custom-fees/${id}`);
      onChange(data.custom_fees);
      toast.success("Removed");
    } catch (err) {
      if (err.response?.status === 404) {
        // Already gone — refresh and pretend it succeeded.
        try {
          const { data } = await API.get("/quote-settings");
          onChange(data.custom_fees || []);
        } catch {}
        toast.success("Removed");
        return;
      }
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div className="space-y-2" data-testid={`custom-fees-${category}`}>
      {items.length > 0 && (
        <div className="space-y-2">
          {items.map((cf) => (
            <div key={cf.id} className={`flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 ${accent}`}
              data-testid={`custom-fee-row-${cf.id}`}>
              <Input
                defaultValue={cf.name}
                onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== cf.name) handleEdit(cf.id, { name: v }); }}
                className="h-7 text-xs flex-1 min-w-[140px] bg-white/70 border-white"
                data-testid={`custom-fee-name-${cf.id}`}
              />
              <div className="relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">£</span>
                <Input
                  type="number" min="0" step="1"
                  defaultValue={cf.amount}
                  onBlur={(e) => { const v = Number(e.target.value); if (!Number.isNaN(v) && v !== cf.amount) handleEdit(cf.id, { amount: v }); }}
                  className="h-7 text-xs pl-5 w-24 bg-white/70 border-white"
                  data-testid={`custom-fee-amount-${cf.id}`}
                />
              </div>
              <div className="flex flex-wrap gap-1">
                {CUSTOM_FEE_TXN_TYPES.map((t) => {
                  const on = (cf.transaction_types || []).includes(t.value);
                  return (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => {
                        const next = on
                          ? (cf.transaction_types || []).filter((x) => x !== t.value)
                          : [...(cf.transaction_types || []), t.value];
                        if (next.length === 0) { toast.error("At least one type required"); return; }
                        handleEdit(cf.id, { transaction_types: next });
                      }}
                      className={`text-[10px] px-1.5 py-0.5 rounded border ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-300"}`}
                      data-testid={`custom-fee-type-${cf.id}-${t.value}`}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={() => handleDelete(cf.id)}
                className="text-slate-500 hover:text-red-600 ml-auto"
                data-testid={`custom-fee-delete-${cf.id}`}
                title="Delete"
              >
                <X weight="bold" className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {adding ? (
        <div className="rounded-md border-2 border-dashed border-slate-300 p-3 space-y-2 bg-slate-50"
          data-testid={`custom-fee-add-form-${category}`}>
          <div className="flex flex-wrap gap-2 items-center">
            <Input
              placeholder="Fee name (e.g. Indemnity Policy)"
              value={name} onChange={(e) => setName(e.target.value)}
              className="h-8 text-xs flex-1 min-w-[180px]"
              data-testid={`custom-fee-new-name-${category}`}
            />
            <div className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">£</span>
              <Input
                type="number" min="0" step="1" placeholder="0"
                value={amount} onChange={(e) => setAmount(e.target.value)}
                className="h-8 text-xs pl-5 w-24"
                data-testid={`custom-fee-new-amount-${category}`}
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className="text-[10px] text-slate-500 uppercase tracking-wide">Apply to:</span>
            {CUSTOM_FEE_TXN_TYPES.map((t) => {
              const on = types.includes(t.value);
              return (
                <button
                  key={t.value} type="button" onClick={() => toggleType(t.value)}
                  className={`text-[10px] px-2 py-0.5 rounded-full border ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-300 hover:border-slate-400"}`}
                  data-testid={`custom-fee-new-type-${category}-${t.value}`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={reset} disabled={busy}
              data-testid={`custom-fee-cancel-${category}`}>Cancel</Button>
            <Button size="sm" className="h-7 text-xs bg-slate-900 hover:bg-slate-800 text-white"
              onClick={handleSave} disabled={busy}
              data-testid={`custom-fee-save-${category}`}>
              {busy ? "Saving…" : "Add"}
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button" onClick={() => setAdding(true)}
          className="text-[11px] font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 px-2 py-1 rounded border border-dashed border-slate-300 hover:border-slate-500 hover:bg-slate-50 transition"
          data-testid={`add-custom-${category}-btn`}
        >
          <Plus weight="bold" className="w-3 h-3" /> Add {labelWord}
        </button>
      )}
    </div>
  );
}

/**
 * Editor for the firm-wide default rows that seed every new case's
 * Conveyancing Fees panel. Three sections (legal_fees, disbursements,
 * third_party); each section is a list of `{label, amount}` pairs.
 * Saving fires PUT /api/case-fees-defaults; the response is the merged
 * doc which we hand back via `onChange` so the parent doesn't have to
 * re-fetch.
 */
function CaseFeesDefaultsSection() {
  const [defaults, setDefaults] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await API.get("/case-fees-defaults");
        if (!cancelled) {
          setDefaults({
            legal_fees: data.legal_fees || [],
            disbursements: data.disbursements || [],
            third_party: data.third_party || [],
          });
          setLoaded(true);
        }
      } catch (err) {
        if (!cancelled) {
          toast.error("Failed to load case fees defaults");
          setLoaded(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const update = (cat, idx, patch) => {
    setDefaults((d) => ({ ...d, [cat]: d[cat].map((r, i) => i === idx ? { ...r, ...patch } : r) }));
  };
  const add = (cat) => setDefaults((d) => ({ ...d, [cat]: [...d[cat], { label: "", amount: 0 }] }));
  const remove = (cat, idx) => setDefaults((d) => ({ ...d, [cat]: d[cat].filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await API.put("/case-fees-defaults", defaults);
      setDefaults({
        legal_fees: data.legal_fees || [],
        disbursements: data.disbursements || [],
        third_party: data.third_party || [],
      });
      toast.success("Case fees defaults saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <p className="text-xs text-slate-400">Loading…</p>;
  if (!defaults) return null;

  const SECTIONS = [
    { key: "legal_fees", title: "Legal fees", hint: "Auto-VAT @ 20%. Seeds new cases." },
    { key: "disbursements", title: "Disbursements", hint: "Pass-through (no auto-VAT)." },
    { key: "third_party", title: "Third-party costs", hint: "No VAT." },
  ];

  return (
    <div className="space-y-4" data-testid="case-fees-defaults-section">
      <p className="text-[11px] text-slate-500">
        These rows are added to every new case's Conveyancing Fees panel. Amounts can be edited per case.
      </p>
      {SECTIONS.map((s) => (
        <div key={s.key} className="space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-800">{s.title}</p>
              <p className="text-[10px] text-slate-400">{s.hint}</p>
            </div>
            <button
              type="button"
              onClick={() => add(s.key)}
              data-testid={`cfd-add-${s.key}`}
              className="text-[11px] font-medium text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 px-2 py-1 rounded border border-dashed border-slate-300 hover:border-slate-500 hover:bg-slate-50 transition"
            >
              <Plus weight="bold" className="w-3 h-3" /> Add row
            </button>
          </div>
          <div className="space-y-1.5">
            {defaults[s.key].length === 0 && (
              <p className="text-[11px] text-slate-400 italic">No default rows.</p>
            )}
            {defaults[s.key].map((r, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_120px_28px] gap-2 items-center" data-testid={`cfd-row-${s.key}-${idx}`}>
                <Input
                  value={r.label}
                  onChange={(e) => update(s.key, idx, { label: e.target.value })}
                  placeholder="Description"
                  className="h-8 text-xs"
                  data-testid={`cfd-row-${s.key}-${idx}-label`}
                />
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">£</span>
                  <Input
                    type="number"
                    step="0.01"
                    value={r.amount}
                    onChange={(e) => update(s.key, idx, { amount: Number(e.target.value) || 0 })}
                    className="h-8 text-xs pl-6 text-right"
                    data-testid={`cfd-row-${s.key}-${idx}-amount`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => remove(s.key, idx)}
                  data-testid={`cfd-row-${s.key}-${idx}-remove`}
                  title="Remove"
                  className="w-7 h-7 flex items-center justify-center rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                >
                  <X weight="bold" className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}
      <Button onClick={save} disabled={saving}
        data-testid="save-case-fees-defaults-btn"
        className="w-full bg-slate-900 hover:bg-slate-800 text-white">
        {saving ? "Saving…" : "Save Case Fees Defaults"}
      </Button>
    </div>
  );
}



export default function QuotesListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterSource, setFilterSource] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [feeSettings, setFeeSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [quoteToLink, setQuoteToLink] = useState(null);
  const [cases, setCases] = useState([]);
  const [selectedCaseId, setSelectedCaseId] = useState("");
  const [linking, setLinking] = useState(false);
  const [instructDialogOpen, setInstructDialogOpen] = useState(false);
  const [quoteToInstruct, setQuoteToInstruct] = useState(null);
  // Recently Deleted view
  const [viewDeleted, setViewDeleted] = useState(false);
  const [deletedQuotes, setDeletedQuotes] = useState([]);
  // Archived view (not-proceeding quotes hidden from the active list
  // but still counted in analytics so conversion rates stay honest).
  const [viewArchived, setViewArchived] = useState(false);
  const [archivedQuotes, setArchivedQuotes] = useState([]);
  // "Not proceeding" dialog — optional reason capture before marking.
  const [notProceedingDialog, setNotProceedingDialog] = useState({ open: false, quote: null, reason: "" });

  const fetchQuotes = useCallback(async () => {
    try {
      const { data } = await API.get("/quotes");
      // Defensive — iteration 67 caught crashes when API returned
      // non-array; every render below chains .filter/.map on `quotes`.
      setQuotes(Array.isArray(data) ? data : []);
    } catch {} finally {
      setLoading(false);
    }
  }, []);

  const fetchDeletedQuotes = useCallback(async () => {
    try {
      const { data } = await API.get("/quotes/deleted");
      setDeletedQuotes(Array.isArray(data) ? data : []);
    } catch {}
  }, []);

  const fetchArchivedQuotes = useCallback(async () => {
    try {
      const { data } = await API.get("/quotes/archived");
      setArchivedQuotes(Array.isArray(data) ? data : []);
    } catch {}
  }, []);

  useEffect(() => {
    fetchQuotes();
    fetchDeletedQuotes();
    fetchArchivedQuotes();
  }, [fetchQuotes, fetchDeletedQuotes, fetchArchivedQuotes]);

  // Log a follow-up touchpoint — increments the counter server-side and
  // refreshes the "last followed up" audit fields. Optimistic UI so the
  // pill flips instantly.
  const handleLogFollowUp = async (q) => {
    const prevCount = q.follow_up_count || 0;
    setQuotes((prev) => prev.map((row) => row.id === q.id
      ? { ...row, follow_up_count: prevCount + 1, last_followed_up_at: new Date().toISOString(), last_followed_up_by_name: "You" }
      : row));
    try {
      const { data } = await API.post(`/quotes/${q.id}/followed-up`);
      setQuotes((prev) => prev.map((row) => row.id === q.id ? {
        ...row,
        follow_up_count: data.follow_up_count ?? (prevCount + 1),
        last_followed_up_at: data.last_followed_up_at,
        last_followed_up_by_name: data.last_followed_up_by_name,
      } : row));
      toast.success(`Follow-up logged (${data.follow_up_count}×)`);
    } catch (err) {
      setQuotes((prev) => prev.map((row) => row.id === q.id
        ? { ...row, follow_up_count: prevCount, last_followed_up_at: q.last_followed_up_at, last_followed_up_by_name: q.last_followed_up_by_name }
        : row));
      toast.error(err.response?.data?.detail || "Failed to log follow-up");
    }
  };

  // Clear the follow-up counter entirely (badge applied to wrong quote,
  // fresh start, etc.). Confirms first because it's destructive.
  const handleResetFollowUps = async (q) => {
    if (!window.confirm(`Reset follow-up counter for ${q.client_name}?`)) return;
    try {
      await API.post(`/quotes/${q.id}/followed-up`, { reset: true });
      setQuotes((prev) => prev.map((row) => row.id === q.id
        ? { ...row, follow_up_count: 0, last_followed_up_at: null, last_followed_up_by_name: null }
        : row));
      toast.success("Follow-up counter reset");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset");
    }
  };

  // Toggle Not Proceeding. Setting it to TRUE opens a dialog so the
  // solicitor can jot down a reason; clearing it is instant (mirrors
  // the /contacted toggle pattern for parity).
  const openNotProceedingDialog = (q) => {
    setNotProceedingDialog({ open: true, quote: q, reason: q.not_proceeding_reason || "" });
  };

  const handleClearNotProceeding = async (q) => {
    try {
      await API.post(`/quotes/${q.id}/not-proceeding`);
      setQuotes((prev) => prev.map((row) => row.id === q.id ? {
        ...row,
        not_proceeding: false,
        not_proceeding_at: null,
        not_proceeding_by_name: null,
        not_proceeding_reason: null,
        is_archived: false,
      } : row));
      // If it was in the archived view, remove it from that list too.
      setArchivedQuotes((prev) => prev.filter((r) => r.id !== q.id));
      toast.success("Marked as proceeding");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const handleConfirmNotProceeding = async () => {
    const q = notProceedingDialog.quote;
    if (!q) return;
    try {
      const { data } = await API.post(`/quotes/${q.id}/not-proceeding`, {
        reason: notProceedingDialog.reason || "",
      });
      setQuotes((prev) => prev.map((row) => row.id === q.id ? {
        ...row,
        not_proceeding: true,
        not_proceeding_at: data.not_proceeding_at,
        not_proceeding_by_name: data.not_proceeding_by_name,
        not_proceeding_reason: data.not_proceeding_reason,
      } : row));
      setNotProceedingDialog({ open: false, quote: null, reason: "" });
      toast.success("Marked as not proceeding");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  // Archive removes the quote from the active list without deleting.
  // Only allowed on not-proceeding quotes (backend enforces this too).
  const handleArchive = async (q) => {
    try {
      await API.post(`/quotes/${q.id}/archive`);
      setQuotes((prev) => prev.filter((row) => row.id !== q.id));
      setArchivedQuotes((prev) => [{ ...q, is_archived: true, archived_at: new Date().toISOString() }, ...prev]);
      toast.success("Quote archived — still counts in analytics");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to archive");
    }
  };

  const handleUnarchive = async (q) => {
    try {
      await API.post(`/quotes/${q.id}/unarchive`);
      setArchivedQuotes((prev) => prev.filter((row) => row.id !== q.id));
      // Push back into the active list so the solicitor sees it
      // immediately. Preserve the not_proceeding flag — clearing that
      // is a separate action.
      setQuotes((prev) => [{ ...q, is_archived: false }, ...prev]);
      toast.success("Quote restored to active list");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to restore");
    }
  };

  // Toggle the "contacted" flag on a public quote. Optimistically updates
  // local state so the badge flips instantly — the server response is the
  // source of truth and the request is fire-and-forget (we revert if it
  // errors).
  const handleToggleContacted = async (q) => {
    const next = !q.contacted_at;
    setQuotes((prev) => prev.map((row) => row.id === q.id
      ? (next
          ? { ...row, contacted_at: new Date().toISOString(), contacted_by_name: "You" }
          : { ...row, contacted_at: null, contacted_by_name: null })
      : row));
    try {
      const { data } = await API.post(`/quotes/${q.id}/contacted`);
      // Reconcile with the server's authoritative response so the
      // tooltip shows the real name/time.
      setQuotes((prev) => prev.map((row) => row.id === q.id ? {
        ...row,
        contacted_at: data.contacted ? (data.contacted_at || row.contacted_at) : null,
        contacted_by_name: data.contacted ? (data.contacted_by_name || row.contacted_by_name) : null,
      } : row));
      toast.success(next ? "Marked as contacted" : "Contacted flag cleared");
    } catch (err) {
      // Revert optimistic update
      setQuotes((prev) => prev.map((row) => row.id === q.id ? { ...row, contacted_at: q.contacted_at, contacted_by_name: q.contacted_by_name } : row));
      toast.error(err.response?.data?.detail || "Failed to update contacted status");
    }
  };

  const handleRestoreQuote = async (id) => {
    try {
      await API.post(`/quotes/${id}/restore`);
      toast.success("Quote restored");
      fetchQuotes();
      fetchDeletedQuotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to restore");
    }
  };

  const handleEmptyTrash = async () => {
    if (deletedQuotes.length === 0) return;
    const c = window.prompt(`Permanently delete ALL ${deletedQuotes.length} quotes in Recently Deleted?\n\nType DELETE to confirm:`);
    if (c !== "DELETE") { toast.info("Cancelled"); return; }
    try {
      const { data } = await API.delete("/quotes/deleted/empty");
      toast.success(`Permanently deleted ${data.purged} quote${data.purged === 1 ? "" : "s"}`);
      fetchDeletedQuotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  // ---------- Bulk selection ----------
  const [selectedActive, setSelectedActive] = useState(new Set());
  const [selectedTrash, setSelectedTrash] = useState(new Set());
  const toggleActive = (id) => {
    const n = new Set(selectedActive); n.has(id) ? n.delete(id) : n.add(id); setSelectedActive(n);
  };
  const toggleTrash = (id) => {
    const n = new Set(selectedTrash); n.has(id) ? n.delete(id) : n.add(id); setSelectedTrash(n);
  };
  const handleBulkSoftDelete = async () => {
    if (!selectedActive.size) return;
    if (!window.confirm(`Move ${selectedActive.size} quote${selectedActive.size === 1 ? "" : "s"} to Recently Deleted?`)) return;
    try {
      const { data } = await API.post("/quotes/bulk-delete", { ids: Array.from(selectedActive) });
      toast.success(`${data.deleted} moved to Recently Deleted`);
      setSelectedActive(new Set());
      fetchQuotes(); fetchDeletedQuotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk delete failed");
    }
  };
  const handleBulkRestoreQuotes = async () => {
    if (!selectedTrash.size) return;
    try {
      const { data } = await API.post("/quotes/bulk-restore", { ids: Array.from(selectedTrash) });
      toast.success(`${data.restored} restored`);
      setSelectedTrash(new Set());
      fetchQuotes(); fetchDeletedQuotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk restore failed");
    }
  };
  const handleBulkPurgeQuotes = async () => {
    if (!selectedTrash.size) return;
    const c = window.prompt(`Permanently delete ${selectedTrash.size} selected quote${selectedTrash.size === 1 ? "" : "s"}?\n\nType DELETE to confirm:`);
    if (c !== "DELETE") return;
    try {
      const { data } = await API.post("/quotes/bulk-permanent-delete", { ids: Array.from(selectedTrash) });
      toast.success(`${data.purged} permanently deleted`);
      setSelectedTrash(new Set());
      fetchDeletedQuotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk purge failed");
    }
  };

  const openSettings = async () => {
    try {
      const { data } = await API.get("/quote-settings");
      setFeeSettings(data);
      setSettingsOpen(true);
    } catch {
      toast.error("Failed to load fee settings");
    }
  };

  const handleSaveSettings = async () => {
    if (!feeSettings) return;
    setSavingSettings(true);
    try {
      // custom_fees is managed by dedicated endpoints (POST/PUT/DELETE
      // /quote-settings/custom-fees) so the array is never overwritten by
      // this PUT. Strip it from the payload to avoid round-trip drift.
      const { custom_fees: _customFees, ...payload } = feeSettings;
      const { data } = await API.put("/quote-settings", payload);
      setFeeSettings(data);
      toast.success("Fee settings saved");
      setSettingsOpen(false);
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const setSetting = (key, val) => setFeeSettings((p) => ({ ...p, [key]: val === "" ? null : parseFloat(val) }));

  // Auto-open the Fee Settings dialog when arrived from the Fee Alert
  // banner (/quotes?settings=fees). Admin-only, since only they can edit.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("settings") === "fees" && user?.is_admin) {
      openSettings();
      window.history.replaceState({}, "", "/quotes");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.is_admin]);

  const openLinkDialog = async (quote) => {
    setQuoteToLink(quote);
    setSelectedCaseId(quote.linked_case_id || "");
    setLinkDialogOpen(true);
    try {
      const { data } = await API.get("/cases");
      setCases(data.filter(c => c.status !== "archived"));
    } catch {}
  };

  const handleLinkCase = async () => {
    if (!quoteToLink || !selectedCaseId) return;
    setLinking(true);
    try {
      const { data } = await API.put(`/quotes/${quoteToLink.id}/link-case`, { case_id: selectedCaseId });
      setLinkDialogOpen(false);
      fetchQuotes();
      if (data.referrer_token) {
        const link = `${window.location.origin}/referrer/${data.referrer_token}`;
        navigator.clipboard.writeText(link);
        toast.success("Case linked. Referrer portal link copied!");
      } else {
        toast.success("Case linked to quote");
      }
    } catch (err) {
      toast.error("Failed to link case");
    } finally {
      setLinking(false);
    }
  };

  const handleCopyReferrerLink = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/referrer/${token}`);
    toast.success("Referrer portal link copied");
  };

  const openInstructDialog = (quote) => {
    setQuoteToInstruct(quote);
    setInstructDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!quoteToDelete) return;
    setDeleting(true);
    try {
      await API.delete(`/quotes/${quoteToDelete.id}`);
      toast.success("Quote moved to Recently Deleted");
      setDeleteDialogOpen(false);
      setQuoteToDelete(null);
      fetchQuotes();
      fetchDeletedQuotes();
    } catch {
      toast.error("Failed to delete quote");
    } finally {
      setDeleting(false);
    }
  };

  const handleCopyLink = (quoteId) => {
    navigator.clipboard.writeText(`${window.location.origin}/quote/view/${quoteId}`);
    toast.success("Quote link copied");
  };

  const filtered = quotes.filter((q) => {
    const matchesSearch = !searchQuery ||
      q.client_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (q.client_email || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (q.referrer_name || "").toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSource = filterSource === "all" || q.source === filterSource;
    // Lifecycle filter — status buckets mirror the icons/pills on
    // each row so a solicitor can jump straight to "who needs
    // chasing" or "who still hasn't opened a case". Buckets are
    // mutually exclusive on the current dominant state (e.g. an
    // instructed quote is 'instructed', not 'contacted', even if
    // both flags exist).
    let matchesStatus = true;
    if (filterStatus !== "all") {
      switch (filterStatus) {
        case "followed_up":
          matchesStatus = (q.follow_up_count || 0) > 0 && !q.instructed;
          break;
        case "case_not_opened":
          // Quote generated but no case ever instructed off the back
          // of it. Excludes not-proceeding leads so this bucket is
          // "genuine open pipeline needing action".
          matchesStatus = !q.instructed && !q.not_proceeding;
          break;
        case "contacted":
          matchesStatus = !!q.contacted_at && !q.instructed && !q.not_proceeding;
          break;
        case "not_contacted":
          matchesStatus = !q.contacted_at && !q.instructed && !q.not_proceeding;
          break;
        case "not_proceeding":
          matchesStatus = !!q.not_proceeding;
          break;
        case "instructed":
          matchesStatus = !!q.instructed;
          break;
        default:
          matchesStatus = true;
      }
    }
    return matchesSearch && matchesSource && matchesStatus;
  });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="quotes-list-page">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <FeeAlertBanner className="mb-6" />
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Quotes
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">{quotes.length} quote{quotes.length !== 1 ? "s" : ""} generated</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {user?.is_admin && (
              <Button onClick={openSettings} variant="outline" className="h-9 text-sm gap-1.5" data-testid="fee-settings-btn">
                <GearSix weight="bold" className="w-4 h-4" /> Fee Settings
              </Button>
            )}
            <Button
              onClick={() => { setViewDeleted(!viewDeleted); setViewArchived(false); }}
              variant="outline" className="h-9 text-sm gap-1.5" data-testid="toggle-deleted-quotes-btn"
            >
              <Trash weight="bold" className="w-4 h-4" />
              {viewDeleted ? "Back to active" : `Recently Deleted${deletedQuotes.length > 0 ? ` (${deletedQuotes.length})` : ""}`}
            </Button>
            <Button
              onClick={() => { setViewArchived(!viewArchived); setViewDeleted(false); }}
              variant="outline" className="h-9 text-sm gap-1.5" data-testid="toggle-archived-quotes-btn"
            >
              <ArchiveIcon weight="bold" className="w-4 h-4" />
              {viewArchived ? "Back to active" : `Archived${archivedQuotes.length > 0 ? ` (${archivedQuotes.length})` : ""}`}
            </Button>
            <Button onClick={() => navigate("/quote")} variant="outline" className="h-9 text-sm gap-1.5" data-testid="new-public-quote-btn">
              <Calculator weight="bold" className="w-4 h-4" /> Public Quote
            </Button>
            <Button onClick={() => navigate("/quotes/custom")} variant="outline" className="h-9 text-sm gap-1.5 border-[#100f5d]/30 text-[#100f5d]" data-testid="new-custom-quote-btn">
              <Calculator weight="bold" className="w-4 h-4" /> Custom Quote
            </Button>
            <Button onClick={() => navigate("/quote/referrer")} className="h-9 text-sm bg-slate-900 hover:bg-slate-800 text-white gap-1.5" data-testid="new-referrer-quote-btn">
              <User weight="bold" className="w-4 h-4" /> Referrer Quote
            </Button>
          </div>
        </div>

        {/* Search & Filter */}
        {!viewDeleted && !viewArchived && (
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              data-testid="quote-search-input"
              placeholder="Search by client, email, or referrer..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-10 bg-white border-slate-200"
            />
          </div>
          <Select value={filterSource} onValueChange={setFilterSource}>
            <SelectTrigger className="w-full sm:w-44 h-10 bg-white border-slate-200" data-testid="filter-source-select">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              <SelectItem value="solicitor">In-firm (Solicitor)</SelectItem>
              <SelectItem value="public">Public Quotes</SelectItem>
              <SelectItem value="referrer">Referrer Quotes</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full sm:w-52 h-10 bg-white border-slate-200" data-testid="filter-status-select">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="case_not_opened">Case not opened</SelectItem>
              <SelectItem value="followed_up">Followed up</SelectItem>
              <SelectItem value="contacted">Contacted (open)</SelectItem>
              <SelectItem value="not_contacted">Not yet contacted</SelectItem>
              <SelectItem value="not_proceeding">Not proceeding</SelectItem>
              <SelectItem value="instructed">Instructed / case opened</SelectItem>
            </SelectContent>
          </Select>
        </div>
        )}

        {/* Quote list — Recently Deleted */}
        {viewDeleted ? (
          <div data-testid="deleted-quotes-section">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <p className="text-xs text-slate-500 font-medium">
                Soft-deleted quotes — restore or empty the trash.
              </p>
              {user?.is_admin && deletedQuotes.length > 0 && (
                <Button onClick={handleEmptyTrash} variant="outline" size="sm"
                  className="h-8 text-xs gap-1.5 border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                  data-testid="empty-quotes-trash-btn">
                  <Trash weight="bold" className="w-3.5 h-3.5" /> Empty Trash ({deletedQuotes.length})
                </Button>
              )}
            </div>
            {deletedQuotes.length === 0 ? (
              <div className="text-center py-12 bg-white border border-slate-200 rounded-lg" data-testid="empty-deleted-quotes">
                <Trash weight="thin" className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500 font-medium">No quotes in Recently Deleted</p>
              </div>
            ) : (
              <>
              <BulkActionBar
                count={selectedTrash.size}
                onClear={() => setSelectedTrash(new Set())}
                onRestore={handleBulkRestoreQuotes}
                onPurge={user?.is_admin ? handleBulkPurgeQuotes : undefined}
                label="quote"
                variant="trash"
                testIdPrefix="quotes-trash"
              />
              <div className="space-y-2">
                {deletedQuotes.map((q) => {
                  const isSel = selectedTrash.has(q.id);
                  return (
                  <div key={q.id} className={`bg-white border rounded-lg p-4 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${isSel ? "border-[#100f5d] ring-2 ring-[#100f5d]/15" : "border-red-100 opacity-80 hover:opacity-100"}`} data-testid={`deleted-quote-row-${q.id}`}>
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <Checkbox checked={isSel} onCheckedChange={() => toggleTrash(q.id)} data-testid={`deleted-quote-cb-${q.id}`} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-900 truncate">{q.client_name}</p>
                        <p className="text-xs text-slate-500 truncate">{q.property_address || "—"}</p>
                        <p className="text-[11px] text-slate-400 mt-1">Deleted {q.deleted_at ? new Date(q.deleted_at).toLocaleString("en-GB") : ""}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 flex-wrap sm:flex-nowrap">
                      <span className="text-sm font-bold text-slate-700">{formatCurrency(q.total)}</span>
                      <Button onClick={() => handleRestoreQuote(q.id)} variant="outline" size="sm" className="h-8 text-xs gap-1.5" data-testid={`restore-quote-${q.id}`}>
                        <ArrowRight weight="bold" className="w-3.5 h-3.5 rotate-180" /> Restore
                      </Button>
                    </div>
                  </div>
                  );
                })}
              </div>
              </>
            )}
          </div>
        ) : viewArchived ? (
          <div data-testid="archived-quotes-section">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <p className="text-xs text-slate-500 font-medium">
                Not-proceeding quotes archived off the active list. Still counted in analytics so conversion rates stay accurate.
              </p>
            </div>
            {archivedQuotes.length === 0 ? (
              <div className="text-center py-12 bg-white border border-slate-200 rounded-lg" data-testid="empty-archived-quotes">
                <ArchiveIcon weight="thin" className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500 font-medium">No archived quotes</p>
              </div>
            ) : (
              <div className="space-y-2">
                {archivedQuotes.map((q) => (
                  <div
                    key={q.id}
                    className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 opacity-80 hover:opacity-100"
                    data-testid={`archived-quote-row-${q.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-slate-900 truncate">{q.client_name}</p>
                        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 inline-flex items-center gap-1">
                          <Prohibit weight="fill" className="w-3 h-3" />
                          Not proceeding
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 truncate">
                        {q.property_address || q.property_type} · {formatCurrency(q.property_value)}
                      </p>
                      {q.not_proceeding_reason && (
                        <p className="text-[11px] text-slate-500 mt-1 italic">
                          Reason: {q.not_proceeding_reason}
                        </p>
                      )}
                      <p className="text-[11px] text-slate-400 mt-1">
                        Archived {q.archived_at ? new Date(q.archived_at).toLocaleString("en-GB") : ""}
                        {q.archived_by_name ? ` by ${q.archived_by_name}` : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 flex-wrap sm:flex-nowrap">
                      <span className="text-sm font-bold text-slate-700">{formatCurrency(q.total)}</span>
                      <Button
                        onClick={() => handleUnarchive(q)}
                        variant="outline" size="sm"
                        className="h-8 text-xs gap-1.5"
                        data-testid={`unarchive-quote-${q.id}`}
                      >
                        <ArrowCounterClockwise weight="bold" className="w-3.5 h-3.5" /> Restore
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-slate-300 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 bg-white border border-slate-200 rounded-lg" data-testid="empty-quotes">
            <Receipt weight="thin" className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500 font-medium">
              {quotes.length === 0 ? "No quotes yet" : "No matching quotes"}
            </p>
          </div>
        ) : (
          <>
            <BulkActionBar
              count={[...selectedActive].filter((id) => filtered.some((q) => q.id === id)).length}
              onClear={() => setSelectedActive(new Set())}
              onDelete={handleBulkSoftDelete}
              label="quote"
              variant="active"
              testIdPrefix="quotes-active"
            />
            <div className="space-y-2">
            {filtered.map((q) => {
              const isSel = selectedActive.has(q.id);
              return (
              <div key={q.id} className={`bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow ${isSel ? "border-[#100f5d] ring-2 ring-[#100f5d]/15" : "border-slate-200"}`} data-testid={`quote-row-${q.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <Checkbox checked={isSel} onCheckedChange={() => toggleActive(q.id)} data-testid={`quote-cb-${q.id}`} className="shrink-0" />
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                      <CurrencyGbp weight="bold" className="w-5 h-5 text-slate-500" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <p className="text-sm font-semibold text-slate-900">{q.client_name}</p>
                        {Array.isArray(q.additional_clients) && q.additional_clients.length > 0 && (
                          <span className="text-[10px] font-medium text-slate-500" data-testid={`quote-client-count-${q.id}`}>
                            +{q.additional_clients.length} joint
                          </span>
                        )}
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                          (q.source === "referrer" || q.source === "referrer_bespoke")
                            ? "bg-violet-50 text-violet-700"
                            : q.source === "solicitor"
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-slate-100 text-slate-600"
                        }`}
                          title={q.created_by_name ? `Generated by ${q.created_by_name}` : undefined}
                          data-testid={`quote-source-${q.id}`}
                        >
                          {(q.source === "referrer" || q.source === "referrer_bespoke")
                            ? `Referrer: ${q.referrer_name}`
                            : q.source === "solicitor"
                              ? `By ${q.created_by_name || "Solicitor"}`
                              : "Public"}
                        </span>
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                          q.transaction_type === "buying" ? "bg-blue-50 text-blue-700" : q.transaction_type === "selling" ? "bg-amber-50 text-amber-700" : q.transaction_type === "grant_of_lease" ? "bg-indigo-50 text-indigo-700" : "bg-cyan-50 text-cyan-700"
                        }`}>
                          {{ buying: "Buying", selling: "Selling", both: "Buying & Selling", remortgage: "Remortgage", transfer: "Transfer of Equity", grant_of_lease: "Grant of Lease" }[q.transaction_type] || q.transaction_type}
                        </span>
                        {q.instructed && (
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700" data-testid={`instructed-badge-${q.id}`}>
                            Case Opened
                          </span>
                        )}
                        {q.client_instructed && !q.instructed && (
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 animate-pulse" data-testid={`client-instructed-badge-${q.id}`}>
                            Client Accepted
                          </span>
                        )}
                        {/* Contacted indicator — green pill with a tooltip
                            showing who marked it + when. Only relevant on
                            quotes that haven't yet been instructed (after
                            instruction the "Case Opened" badge supersedes
                            this lifecycle stage). */}
                        {q.contacted_at && !q.instructed && (
                          <span
                            className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700 inline-flex items-center gap-1"
                            data-testid={`contacted-badge-${q.id}`}
                            title={`Contacted ${q.contacted_by_name ? `by ${q.contacted_by_name} ` : ""}on ${new Date(q.contacted_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`}
                          >
                            <CheckCircle weight="fill" className="w-3 h-3" />
                            Contacted
                          </span>
                        )}
                        {/* Followed-up pill — counts each explicit
                            chase after first contact. Only relevant on
                            uninstructed quotes; a case being open makes
                            it moot. Blue palette to distinguish it
                            visually from the initial "Contacted" step. */}
                        {(q.follow_up_count > 0) && !q.instructed && (
                          <span
                            className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 inline-flex items-center gap-1"
                            data-testid={`followed-up-badge-${q.id}`}
                            title={
                              q.last_followed_up_at
                                ? `Last chased ${q.last_followed_up_by_name ? `by ${q.last_followed_up_by_name} ` : ""}on ${new Date(q.last_followed_up_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`
                                : "Chased"
                            }
                          >
                            <FollowUpIcon weight="fill" className="w-3 h-3" />
                            Followed up {q.follow_up_count}×
                          </span>
                        )}
                        {/* Not-proceeding pill — client declined. Red
                            palette to draw the eye so it doesn't get
                            missed. Reason (if provided) surfaces via
                            the tooltip so the badge itself stays
                            compact. */}
                        {q.not_proceeding && (
                          <span
                            className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 inline-flex items-center gap-1"
                            data-testid={`not-proceeding-badge-${q.id}`}
                            title={
                              q.not_proceeding_reason
                                ? `Not proceeding — ${q.not_proceeding_reason}${q.not_proceeding_by_name ? ` (${q.not_proceeding_by_name})` : ""}`
                                : `Not proceeding${q.not_proceeding_by_name ? ` — ${q.not_proceeding_by_name}` : ""}`
                            }
                          >
                            <Prohibit weight="fill" className="w-3 h-3" />
                            Not proceeding
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                        <span>Property: {formatCurrency(q.property_value)}</span>
                        <span>·</span>
                        <span className="capitalize">{q.property_type}</span>
                        <span>·</span>
                        <span>{new Date(q.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                      </div>
                      {/* Per-client contact lines — every quote client gets
                          their own row of click-to-call/email links so the
                          solicitor can reach each joint applicant directly
                          from the list. Primary client first, then any
                          additional clients in the order they were saved. */}
                      <QuoteContacts q={q} />
                    </div>
                  </div>
                    <div className="flex items-center gap-3 shrink-0 sm:ml-4 flex-wrap">
                    <p className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{formatCurrency(q.total)}</p>
                    {!q.instructed && (
                      <button onClick={() => openInstructDialog(q)} data-testid={`instruct-quote-${q.id}`}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                          q.client_instructed ? "text-white bg-emerald-600 hover:bg-emerald-700" : "text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
                        }`}
                        title="Create case from quote">
                        {q.client_instructed ? "Open Case" : "Instruct"}
                      </button>
                    )}
                    <div className="flex items-center gap-0.5">
                      {/* Toggle "contacted" — only on quotes that haven't
                          been instructed yet, since after instruction the
                          contacted state is implicit. Yellow ring on
                          uncontacted quotes makes them stand out so the
                          team knows what still needs follow-up. */}
                      {!q.instructed && (
                        <button
                          onClick={() => handleToggleContacted(q)}
                          data-testid={`toggle-contacted-${q.id}`}
                          className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
                            q.contacted_at
                              ? "text-green-600 hover:bg-green-100"
                              : "text-amber-500 hover:bg-amber-100"
                          }`}
                          title={q.contacted_at ? "Mark as not contacted" : "Mark as contacted"}
                        >
                          {q.contacted_at
                            ? <CheckCircle weight="fill" className="w-4 h-4" />
                            : <CircleDashed weight="bold" className="w-4 h-4" />}
                        </button>
                      )}
                      {/* Log a follow-up — visible on every uninstructed
                          quote. Left-click increments; shift/right-click
                          resets so mis-clicks are recoverable. */}
                      {!q.instructed && (
                        <button
                          onClick={(e) => e.shiftKey ? handleResetFollowUps(q) : handleLogFollowUp(q)}
                          onContextMenu={(e) => { e.preventDefault(); handleResetFollowUps(q); }}
                          data-testid={`log-followup-${q.id}`}
                          className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
                            q.follow_up_count > 0
                              ? "text-sky-600 hover:bg-sky-100"
                              : "text-slate-400 hover:bg-sky-100 hover:text-sky-600"
                          }`}
                          title={q.follow_up_count > 0
                            ? `Log another follow-up (${q.follow_up_count}× so far) — Shift-click to reset`
                            : "Log a follow-up — Shift-click to reset"}
                        >
                          <FollowUpIcon weight="bold" className="w-4 h-4" />
                        </button>
                      )}
                      {/* Not proceeding — toggle. Setting opens a small
                          dialog to capture the reason; clearing is
                          instant. Hidden on already-instructed quotes
                          (a case has been opened, the lead did in fact
                          proceed). */}
                      {!q.instructed && (
                        <button
                          onClick={() => q.not_proceeding ? handleClearNotProceeding(q) : openNotProceedingDialog(q)}
                          data-testid={`toggle-not-proceeding-${q.id}`}
                          className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
                            q.not_proceeding
                              ? "text-rose-600 hover:bg-rose-100"
                              : "text-slate-400 hover:bg-rose-100 hover:text-rose-600"
                          }`}
                          title={q.not_proceeding ? "Mark as proceeding" : "Mark as not proceeding"}
                        >
                          <Prohibit weight="bold" className="w-4 h-4" />
                        </button>
                      )}
                      {/* Archive — only surfaced on not-proceeding
                          quotes, per user's spec. Analytics still count
                          these rows, so conversion rates aren't skewed
                          by hiding dead leads. */}
                      {q.not_proceeding && !q.instructed && (
                        <button
                          onClick={() => handleArchive(q)}
                          data-testid={`archive-quote-${q.id}`}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-200 text-slate-400 hover:text-slate-700 transition-colors"
                          title="Archive — hides from active list, still counts in analytics"
                        >
                          <ArchiveIcon weight="bold" className="w-4 h-4" />
                        </button>
                      )}
                      {(q.source === "referrer" || q.source === "referrer_bespoke") && (
                        <>
                          {q.referrer_token ? (
                            <button onClick={() => handleCopyReferrerLink(q.referrer_token)} data-testid={`referrer-link-${q.id}`}
                              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-violet-100 text-violet-400 hover:text-violet-600 transition-colors"
                              title="Copy referrer portal link">
                              <LinkSimple weight="bold" className="w-3.5 h-3.5" />
                            </button>
                          ) : (
                            <button onClick={() => openLinkDialog(q)} data-testid={`link-case-${q.id}`}
                              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-blue-100 text-slate-400 hover:text-blue-600 transition-colors"
                              title="Link to case">
                              <LinkSimple weight="bold" className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </>
                      )}
                      <button onClick={() => handleCopyLink(q.id)} data-testid={`copy-quote-${q.id}`}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 hover:text-blue-600 transition-colors"
                        title="Copy share link">
                        <Copy weight="bold" className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => navigate(`/quote/view/${q.id}`)} data-testid={`view-quote-${q.id}`}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                        title="View quote">
                        <ArrowRight weight="bold" className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          // Ask the backend for the correct prefill URL —
                          // it resolves the right tool (bespoke / legacy
                          // referrer / public) and any referrer token
                          // server-side, so we mirror the regenerate
                          // behaviour of the referrer dashboard without
                          // exposing referrer tokens to the solicitor SPA.
                          try {
                            const { data } = await API.get(`/quotes/${q.id}/regenerate-url`);
                            window.open(data.url, "_blank", "noopener,noreferrer");
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Could not open quote tool");
                          }
                        }}
                        data-testid={`regenerate-quote-${q.id}`}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-amber-50 text-slate-400 hover:text-amber-600 transition-colors"
                        title="Re-generate this quote (opens the original tool prefilled)"
                        aria-label="Re-generate quote"
                      >
                        <ArrowsClockwise weight="bold" className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => { setQuoteToDelete(q); setDeleteDialogOpen(true); }} data-testid={`delete-quote-${q.id}`}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-100 text-slate-400 hover:text-red-600 transition-colors"
                        title="Delete quote">
                        <Trash weight="bold" className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
          </>
        )}

        {/* Delete Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Delete Quote</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {quoteToDelete && (
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                  <p className="text-sm font-medium text-slate-900">{quoteToDelete.client_name}</p>
                  <p className="text-xs text-slate-500">{formatCurrency(quoteToDelete.total)} · {new Date(quoteToDelete.created_at).toLocaleDateString("en-GB")}</p>
                </div>
              )}
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="flex-1">Cancel</Button>
                <Button onClick={handleDelete} disabled={deleting} data-testid="confirm-delete-quote-btn"
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white">
                  {deleting ? "Deleting..." : "Delete"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Not Proceeding Dialog — optional reason capture. Reason is
            free-text and shows up in the pill tooltip + archived view
            so the team remembers why a lead died. */}
        <Dialog
          open={notProceedingDialog.open}
          onOpenChange={(open) => !open && setNotProceedingDialog({ open: false, quote: null, reason: "" })}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Mark as Not Proceeding</DialogTitle>
              <DialogDescription className="text-xs text-slate-500">
                Optionally record why. The quote stays in analytics so conversion rates don&apos;t skew.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {notProceedingDialog.quote && (
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                  <p className="text-sm font-medium text-slate-900">
                    {notProceedingDialog.quote.client_name}
                  </p>
                  <p className="text-xs text-slate-500">
                    {formatCurrency(notProceedingDialog.quote.total)}
                    {" · "}
                    {new Date(notProceedingDialog.quote.created_at).toLocaleDateString("en-GB")}
                  </p>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="not-proceeding-reason" className="text-xs font-medium text-slate-700">
                  Reason (optional)
                </Label>
                <Input
                  id="not-proceeding-reason"
                  data-testid="not-proceeding-reason-input"
                  value={notProceedingDialog.reason}
                  onChange={(e) => setNotProceedingDialog((s) => ({ ...s, reason: e.target.value }))}
                  placeholder="e.g. Went with competitor, sale fell through"
                  maxLength={500}
                />
                <p className="text-[11px] text-slate-400">
                  The quote stays in analytics so conversion rates aren&apos;t skewed. You can archive it from the list to clean up your view.
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setNotProceedingDialog({ open: false, quote: null, reason: "" })}
                  className="flex-1"
                  data-testid="not-proceeding-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmNotProceeding}
                  data-testid="not-proceeding-confirm-btn"
                  className="flex-1 bg-rose-600 hover:bg-rose-700 text-white"
                >
                  Mark not proceeding
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Fee Settings Dialog */}
        <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Quote Fee Settings</DialogTitle>
            </DialogHeader>
            {feeSettings && (
              <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-1">
                <div>
                  <h3 className="text-xs font-semibold text-slate-900 uppercase tracking-wide mb-3">Base Legal Fees (excl. VAT)</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-3">
                    {[
                      { key: "legal_fee_buying", label: "Buying" },
                      { key: "legal_fee_selling", label: "Selling" },
                      { key: "legal_fee_both", label: "Buy & Sell (combined)" },
                      { key: "legal_fee_both_purchase", label: "Buy & Sell — Purchase leg" },
                      { key: "legal_fee_both_sale", label: "Buy & Sell — Sale leg" },
                      { key: "legal_fee_remortgage", label: "Remortgage" },
                      { key: "legal_fee_transfer", label: "Transfer of Equity" },
                      { key: "legal_fee_grant_of_lease", label: "Grant of Lease" },
                    ].map((f) => (
                      <div key={f.key} className="space-y-1 min-w-0">
                        <Label className="text-[10px] font-medium text-slate-500 leading-tight block truncate" title={f.label}>{f.label}</Label>
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">£</span>
                          <Input data-testid={`setting-${f.key}`} type="number" min="0" step="1"
                            value={feeSettings[f.key] ?? ""} onChange={(e) => setSetting(f.key, e.target.value)}
                            className="h-9 text-sm pl-6 w-full" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <hr className="border-slate-100" />
                <div className="space-y-5">
                  <h3 className="text-xs font-semibold text-slate-900 uppercase tracking-wide">Supplementary Fees (excl. VAT)</h3>
                  {[
                    {
                      tag: "Leasehold (per transaction type)",
                      tagColor: "bg-indigo-50 text-indigo-700 border-indigo-200",
                      hint: "Replaces the legacy single Leasehold fee. Leave blank to inherit.",
                      fields: [
                        { key: "leasehold_fee_buying", label: "Buying" },
                        { key: "leasehold_fee_selling", label: "Selling" },
                        { key: "leasehold_fee_both", label: "Buy & Sell" },
                        { key: "leasehold_fee_remortgage", label: "Remortgage" },
                        { key: "leasehold_fee_transfer", label: "Transfer of Equity" },
                        { key: "leasehold_fee_grant_of_lease", label: "Grant of Lease" },
                      ],
                    },
                    {
                      tag: "BSA (per transaction type)",
                      tagColor: "bg-emerald-50 text-emerald-700 border-emerald-200",
                      hint: "Universal BSA toggle on every quote tool — fee picked from the matching type below.",
                      fields: [
                        { key: "bsa_fee_buying", label: "Buying" },
                        { key: "bsa_fee_selling", label: "Selling" },
                        { key: "bsa_fee_both", label: "Buy & Sell" },
                        { key: "bsa_fee_remortgage", label: "Remortgage" },
                        { key: "bsa_fee_transfer", label: "Transfer of Equity" },
                        { key: "bsa_fee_grant_of_lease", label: "Grant of Lease" },
                      ],
                    },
                    {
                      tag: "Purchase",
                      tagColor: "bg-blue-50 text-blue-700 border-blue-200",
                      fields: [
                        { key: "lender_fee", label: "Mortgage Lender Fee" },
                        { key: "new_build_supplement", label: "New Build Supplement" },
                        { key: "gifted_deposit_fee_per_giftor", label: "Gifted Deposit (per giftor)" },
                        { key: "htb_isa_fee", label: "Help to Buy ISA (per ISA)" },
                        { key: "lifetime_isa_fee", label: "Lifetime ISA (per ISA)" },
                      ],
                    },
                    {
                      tag: "Sale",
                      tagColor: "bg-amber-50 text-amber-700 border-amber-200",
                      fields: [
                        { key: "mortgage_redemption_fee", label: "Mortgage Redemption" },
                        { key: "htb_loan_redemption_fee", label: "HTB Loan Redemption" },
                      ],
                    },
                    {
                      tag: "Transfer of Equity",
                      tagColor: "bg-violet-50 text-violet-700 border-violet-200",
                      fields: [
                        { key: "new_mortgage_fee", label: "New Mortgage" },
                      ],
                    },
                    {
                      tag: "Universal",
                      tagColor: "bg-slate-100 text-slate-600 border-slate-200",
                      hint: "Apply across multiple transaction types",
                      fields: [
                        { key: "shared_ownership_supplement", label: "Shared Ownership" },
                        { key: "ltd_company_supplement", label: "Ltd Company" },
                        { key: "auction_supplement", label: "Auction" },
                        { key: "right_to_buy_supplement", label: "Right to Buy" },
                        { key: "commercial_supplement", label: "Commercial / Mixed-Use" },
                        { key: "existing_mortgage_fee", label: "Existing Mortgage (Remo + TOE)" },
                        { key: "leasehold_fee", label: "Leasehold (legacy fallback)" },
                        { key: "bsa_fee", label: "BSA (legacy fallback)" },
                      ],
                    },
                  ].map((group) => (
                    <div key={group.tag} className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold tracking-wide uppercase ${group.tagColor}`} data-testid={`fee-group-${group.tag.toLowerCase().replace(/\s+/g, '-')}`}>
                          {group.tag}
                        </span>
                        {group.hint && <span className="text-[10px] text-slate-400">{group.hint}</span>}
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-3 pl-1">
                        {group.fields.map((f) => (
                          <div key={f.key} className="space-y-1 min-w-0">
                            <Label className="text-[10px] font-medium text-slate-500 leading-tight block truncate" title={f.label}>{f.label}</Label>
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">£</span>
                              <Input data-testid={`setting-${f.key}`} type="number" min="0" step="1"
                                value={feeSettings[f.key] ?? ""} onChange={(e) => setSetting(f.key, e.target.value)}
                                className="h-9 text-sm pl-6 w-full" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  <CustomFeesSection
                    category="supplementary"
                    customFees={feeSettings.custom_fees}
                    onChange={(arr) => setFeeSettings((p) => ({ ...p, custom_fees: arr }))}
                  />
                </div>
                <hr className="border-slate-100" />
                <div>
                  <h3 className="text-xs font-semibold text-slate-900 uppercase tracking-wide mb-3">Disbursement Fees</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-3">
                    {[
                      { key: "search_pack", label: "Search Pack" },
                      { key: "land_registry_search", label: "Land Registry Search" },
                      { key: "bankruptcy_search", label: "Bankruptcy Search" },
                      { key: "title_deeds", label: "Title Deeds" },
                      { key: "bank_transfer_fee", label: "Bank Transfer Fee" },
                      { key: "aml_id_verification", label: "AML / ID Verification" },
                    ].map((f) => (
                      <div key={f.key} className="space-y-1 min-w-0">
                        <Label className="text-[10px] font-medium text-slate-500 leading-tight block truncate" title={f.label}>{f.label}</Label>
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">£</span>
                          <Input data-testid={`setting-${f.key}`} type="number" min="0" step="1"
                            value={feeSettings[f.key] ?? ""} onChange={(e) => setSetting(f.key, e.target.value)}
                            className="h-9 text-sm pl-6 w-full" />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3">
                    <CustomFeesSection
                      category="disbursement"
                      customFees={feeSettings.custom_fees}
                      onChange={(arr) => setFeeSettings((p) => ({ ...p, custom_fees: arr }))}
                    />
                  </div>
                </div>
                <hr className="border-slate-100" />
                <div>
                  <h3 className="text-xs font-semibold text-slate-900 uppercase tracking-wide mb-3">Case Fees Panel — Default Rows</h3>
                  <CaseFeesDefaultsSection />
                </div>
                <Button onClick={handleSaveSettings} disabled={savingSettings}
                  data-testid="save-fee-settings-btn"
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white">
                  {savingSettings ? "Saving..." : "Save Settings"}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Link Case Dialog */}
        <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Link Quote to Case</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {quoteToLink && (
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                  <p className="text-sm font-medium text-slate-900">{quoteToLink.client_name}</p>
                  <p className="text-xs text-slate-500">{quoteToLink.referrer_name ? `Referrer: ${quoteToLink.referrer_name}` : "Public quote"}</p>
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-xs font-medium">Select a case</Label>
                <Select value={selectedCaseId} onValueChange={setSelectedCaseId}>
                  <SelectTrigger data-testid="link-case-select"><SelectValue placeholder="Choose case..." /></SelectTrigger>
                  <SelectContent>
                    {cases.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.title} - {c.property_address}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {quoteToLink?.referrer_name && (
                <p className="text-xs text-violet-600 font-medium">
                  A referrer portal link will be generated so {quoteToLink.referrer_name} can track milestones.
                </p>
              )}
              <Button onClick={handleLinkCase} disabled={linking || !selectedCaseId}
                data-testid="confirm-link-case-btn"
                className="w-full bg-slate-900 hover:bg-slate-800 text-white">
                {linking ? "Linking..." : "Link Case"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Instruct Dialog */}
        {quoteToInstruct && (
          <InstructDialog open={instructDialogOpen} onOpenChange={(v) => { setInstructDialogOpen(v); if (!v) setQuoteToInstruct(null); }}
            quoteId={quoteToInstruct.id} quoteName={quoteToInstruct.client_name} />
        )}
      </main>
    </div>
  );
}
